import { cx } from '@emotion/css';
import React, { useState, useEffect, useMemo } from 'react';
import { ix } from '../../../../utils/ix';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '../../../atoms';
import { Alert } from '../../alerts';
import useStyles from './ColumnSelector.styles';
import { ColumnSelectorCard } from './components/ColumnSelectorCard';
import { AVAILABLE_CARD_ID, AVAILABLE_CARD_TITLE, BTN_APPLY_ID, BTN_CANCEL_ID, EVENTS_CARD_ID, EVENT_CARD_TITLE, EVENT_COLUMN, METRICS_CARD_ID, METRIC_CARD_TITLE, RESTRICTION_BANNER_ID, SELECTED_CARD_ID, SELECTED_CARD_TITLE, } from './constants';
import useColumnSelector from './hooks/UseColumnSelector';
import { sortEventsByPopularity } from './utils';
export function ColumnSelector(props) {
    const { columns, onClose, onSave, onItemPin, onItemUnpin, onValidate, eventsPopularity, minimumSelectedColumns, maximumSelectedColumns, onSelectedColumns, onAddMetric, } = props;
    const classes = useStyles();
    const { metrics, events, selected, showMetricsColumn, setEvents, setSelected, setMetrics } = useColumnSelector(columns);
    const [errorMessage, setErrorMessage] = useState();
    const GRID_LAYOUT = !showMetricsColumn ? classes.smallGrid : classes.largeGrid;
    const validateSelected = useMemo(() => {
        return (selections) => {
            const customError = onValidate === null || onValidate === void 0 ? void 0 : onValidate(selections);
            if (customError) {
                return customError;
            }
            else if (selections.some((col) => col.disabled)) {
                return 'Custom metrics containing unavailable events/metics must be removed to continue.';
            }
            else if (selections.length < minimumSelectedColumns || selections.length > maximumSelectedColumns) {
                const range = `${minimumSelectedColumns}-${maximumSelectedColumns}`;
                return `We are currently only supporting an amount of ${range} selected columns.`;
            }
        };
    }, [onValidate, minimumSelectedColumns, maximumSelectedColumns]);
    useEffect(() => {
        const error = validateSelected(selected);
        setErrorMessage(error);
    }, [selected, validateSelected]);
    const onApply = () => {
        if (errorMessage || validateSelected(selected)) {
            return;
        }
        if (onSelectedColumns)
            onSelectedColumns(selected);
        onSave === null || onSave === void 0 ? void 0 : onSave(selected, true);
    };
    const onSelectItem = (item) => {
        item = Object.assign(Object.assign({}, item), { selected: true });
        if (item.type === EVENT_COLUMN) {
            const newEvents = events.filter((column) => column.value !== item.value);
            setEvents(newEvents);
            setSelected([...selected, item]);
        }
        else {
            const newMetrics = metrics.filter((column) => column.value !== item.value);
            setMetrics(newMetrics);
            const alwaysEnabledColumns = selected.filter((column) => column.alwaysEnabled);
            const notAlwaysEnabledColumns = selected.filter((column) => !column.alwaysEnabled);
            setSelected([...alwaysEnabledColumns, item, ...notAlwaysEnabledColumns]);
        }
    };
    const onUnselectItem = (item) => {
        item = Object.assign(Object.assign({}, item), { selected: false });
        if (item.type === EVENT_COLUMN) {
            setEvents([...events, item]);
        }
        else {
            setMetrics([...metrics, item].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)));
        }
        setSelected(selected.filter((column) => column.value !== item.value));
    };
    const onPinEvent = (item) => {
        item = Object.assign(Object.assign({}, item), { pinned: true });
        setEvents(events.filter((column) => column.value !== item.value).concat(item));
        onItemPin === null || onItemPin === void 0 ? void 0 : onItemPin(item);
        onSave === null || onSave === void 0 ? void 0 : onSave(selected, false);
    };
    const onPinMetric = (item) => {
        item = Object.assign(Object.assign({}, item), { pinned: true });
        setMetrics(metrics.filter((column) => column.value !== item.value).concat(item));
        onItemPin === null || onItemPin === void 0 ? void 0 : onItemPin(item);
        onSave === null || onSave === void 0 ? void 0 : onSave(selected, false);
    };
    const onUnpinEvent = (item) => {
        item = Object.assign(Object.assign({}, item), { pinned: false });
        setEvents(events.filter((column) => column.value !== item.value).concat(item));
        onItemUnpin === null || onItemUnpin === void 0 ? void 0 : onItemUnpin(item);
        onSave === null || onSave === void 0 ? void 0 : onSave(selected, false);
    };
    const onUnpinMetric = (item) => {
        item = Object.assign(Object.assign({}, item), { pinned: false });
        setMetrics(metrics.filter((column) => column.value !== item.value).concat(item));
        onItemUnpin === null || onItemUnpin === void 0 ? void 0 : onItemUnpin(item);
        onSave === null || onSave === void 0 ? void 0 : onSave(selected, false);
    };
    const onSelectedItemsMove = (reorderedItems) => {
        setSelected(reorderedItems);
    };
    const renderRestrictionBanner = () => {
        if (!errorMessage) {
            return null;
        }
        return (React.createElement(Alert, { id: ix(props.id, RESTRICTION_BANNER_ID), variant: "error", className: classes.restrictionBanner }, errorMessage));
    };
    const renderEventsColumn = () => {
        const id = showMetricsColumn ? EVENTS_CARD_ID : AVAILABLE_CARD_ID;
        const title = showMetricsColumn ? EVENT_CARD_TITLE : AVAILABLE_CARD_TITLE;
        const sortedEvents = sortEventsByPopularity(events, eventsPopularity);
        return (React.createElement(ColumnSelectorCard, { id: ix(props.id, id), title: title, titleColor: "black-pearl", columns: sortedEvents, onItemClick: onSelectItem, onItemPin: onPinEvent, onItemUnpin: onUnpinEvent }));
    };
    const renderMetricsColumn = () => {
        if (!showMetricsColumn) {
            return null;
        }
        return (React.createElement(ColumnSelectorCard, { id: ix(props.id, METRICS_CARD_ID), title: METRIC_CARD_TITLE, titleColor: "black-pearl", columns: metrics, onItemClick: onSelectItem, onItemPin: onPinMetric, onItemUnpin: onUnpinMetric, onAddColumn: onAddMetric }));
    };
    return (React.createElement(Dialog, { open: props.open, onClose: () => onClose === null || onClose === void 0 ? void 0 : onClose(), id: props.id, className: cx(props.className, classes.dialog) },
        React.createElement(DialogTitle, null, "Customize Columns"),
        React.createElement(DialogContent, { topDivider: true, bottomDivider: true },
            renderRestrictionBanner(),
            React.createElement("div", { className: GRID_LAYOUT },
                renderEventsColumn(),
                renderMetricsColumn(),
                React.createElement(ColumnSelectorCard, { id: ix(props.id, SELECTED_CARD_ID), title: SELECTED_CARD_TITLE, titleColor: "java", columns: selected, onItemClick: onUnselectItem, onItemMove: onSelectedItemsMove, draggable: true }))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { id: ix(props.id, BTN_CANCEL_ID), color: "primary", variant: "outlined", onClick: () => onClose === null || onClose === void 0 ? void 0 : onClose() }, "Cancel"),
            React.createElement(Button, { id: ix(props.id, BTN_APPLY_ID), color: "primary", variant: "contained", onClick: onApply, disabled: !!errorMessage }, "Apply"))));
}
//# sourceMappingURL=ColumnSelector.js.map